/**
 * moveEffects — a PROCEDURAL move-effect VFX engine. No art assets: every effect
 * is drawn live with Phaser Graphics / Particles / Tweens, tinted by the move's
 * elemental type (and given a bespoke flourish for famous moves). One async entry
 * point, `playMoveEffect`, plays an effect and resolves when it finishes.
 *
 * Design contract:
 *   • CATEGORY templates (all type-coloured):
 *       - special  -> a glowing beam/projectile from→to + an impact burst.
 *       - physical -> a quick lunge cue + an impact flash + brief shake + a hit spark.
 *       - status   -> an aura/glow pulse on the user (from); no projectile.
 *   • SIGNATURE overrides for famous moves (Hyper Beam, Ice Beam, Thunderbolt,
 *     Thunder, Flamethrower, Solar Beam, Hydro Pump, Blizzard) that feel special.
 *   • `reduced` (reduced-motion) -> a short, gentle flash with minimal motion.
 *   • `pace` -> scales every duration (turbo = faster).
 *   • SELF-CLEANING: every Graphics / ParticleEmitter / Tween / Timer this spawns
 *     is tracked and destroyed when the effect ends, and the whole thing is safe
 *     if the scene shuts down mid-effect (it tears down and resolves immediately).
 *
 * The DECISION logic (type→colour, category→template, signature detection, phase
 * timing) lives in the Phaser-free `moveEffects.logic.ts` so it is unit-testable
 * in the node env; this file is the drawing/animation half only.
 */
import Phaser from 'phaser';
import type { PokemonType, MoveCategory } from '@/shared';
import { resolveEffectPlan, type EffectPlan, type EffectTimings } from './moveEffects.logic.js';

export { TYPE_COLORS } from './moveEffects.logic.js';

/** A point in scene space (the active sprite anchors the battle layer passes in). */
export interface EffectPoint {
  readonly x: number;
  readonly y: number;
}

/** Options for {@link playMoveEffect}. */
export interface MoveEffectOptions {
  readonly moveId: string;
  readonly moveName: string;
  readonly type: PokemonType;
  readonly category: MoveCategory;
  /** The attacker's anchor (beam origin / aura centre / lunge start). */
  readonly from: EffectPoint;
  /** The defender's anchor (beam/impact target / lunge destination). */
  readonly to: EffectPoint;
  /** Reduced-motion: a brief, gentle version. */
  readonly reduced: boolean;
  /** Duration multiplier (turbo < 1 = faster). */
  readonly pace: number;
  /** Optional render depth for every spawned object (default high, above sprites). */
  readonly depth?: number;
}

const DEFAULT_DEPTH = 1000;
/** The shared 1×1 white particle texture key (generated once per scene). */
const PARTICLE_TEX = '__mfx_particle';

/**
 * Play a move's effect. Resolves when the effect finishes AND every object it
 * spawned has been destroyed. Never rejects; if the scene is gone (or goes away
 * mid-flight) it resolves promptly after cleaning up whatever it created.
 */
export async function playMoveEffect(scene: Phaser.Scene, opts: MoveEffectOptions): Promise<void> {
  // Bail immediately if there's nothing alive to draw on.
  if (!scene || !scene.sys || !scene.sys.isActive()) return;

  const plan = resolveEffectPlan({
    moveId: opts.moveId,
    moveName: opts.moveName,
    type: opts.type,
    category: opts.category,
    reduced: opts.reduced,
    pace: opts.pace,
  });

  const fx = new EffectRun(scene, opts, plan);
  try {
    await fx.run();
  } finally {
    fx.destroy();
  }
}

/**
 * One in-flight effect. Owns every object/tween/timer it spawns so teardown is a
 * single sweep. A SHUTDOWN/DESTROY listener guarantees cleanup + resolution even
 * if the scene dies mid-effect.
 */
class EffectRun {
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly tweens: Phaser.Tweens.Tween[] = [];
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private readonly emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private done = false;
  private resolveRun: (() => void) | null = null;
  private readonly onShutdown = (): void => this.abort();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly opts: MoveEffectOptions,
    private readonly plan: EffectPlan,
  ) {
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
    scene.events.once(Phaser.Scenes.Events.DESTROY, this.onShutdown);
  }

  /** True only while the scene is alive and this run hasn't been torn down. */
  private get alive(): boolean {
    return !this.done && !!this.scene.sys && this.scene.sys.isActive();
  }

  /** The depth every spawned object renders at (above the combatant sprites). */
  private get depth(): number {
    return this.opts.depth ?? DEFAULT_DEPTH;
  }

  // ── Orchestration ────────────────────────────────────────────────────────────

  run(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resolveRun = resolve;
      if (!this.alive) {
        this.finish();
        return;
      }
      void this.play();
    });
  }

  private async play(): Promise<void> {
    try {
      ensureParticleTexture(this.scene);
      if (this.plan.reduced) {
        await this.playReduced();
      } else if (this.plan.signature) {
        await this.playSignature();
      } else {
        await this.playByTemplate();
      }
    } catch {
      // Swallow — a destroyed scene mid-tween can throw; teardown handles it.
    }
    this.finish();
  }

  private async playByTemplate(): Promise<void> {
    switch (this.plan.template) {
      case 'beam':
        await this.beam(this.plan.color, this.plan.coreColor);
        return;
      case 'lunge-hit':
        await this.lungeHit(this.plan.color);
        return;
      case 'aura':
        await this.aura(this.plan.color);
        return;
      default:
        await this.aura(this.plan.color);
    }
  }

  private async playSignature(): Promise<void> {
    const t = this.plan.timings;
    switch (this.plan.signature) {
      case 'hyper-beam':
        await this.chargeGlow(this.plan.color, t.charge);
        await this.beam(this.plan.color, this.plan.coreColor, { thick: 16, big: true });
        return;
      case 'solar-beam':
        await this.chargeGlow(0xfff080, t.charge);
        await this.beam(this.plan.color, this.plan.coreColor, { thick: 12, big: true });
        return;
      case 'ice-beam':
        await this.beam(this.plan.color, this.plan.coreColor, { thick: 9 });
        await this.frostShards(this.plan.color);
        return;
      case 'flamethrower':
        await this.fireStream(this.plan.color);
        return;
      case 'thunderbolt':
        await this.bolts(this.plan.color, 3);
        return;
      case 'thunder':
        await this.bolts(this.plan.color, 5, { fromAbove: true });
        return;
      case 'hydro-pump':
        await this.beam(this.plan.color, this.plan.coreColor, { thick: 13 });
        await this.impactBurst(this.plan.color);
        return;
      case 'blizzard':
        await this.fireStream(this.plan.color, { swirl: true });
        await this.frostShards(this.plan.color);
        return;
      default:
        await this.beam(this.plan.color, this.plan.coreColor);
    }
  }

  /** Reduced-motion: a single quick, gentle flash near the relevant anchor. */
  private async playReduced(): Promise<void> {
    const anchor = this.plan.template === 'aura' ? this.opts.from : this.opts.to;
    const g = this.gfx();
    if (!g) return;
    g.fillStyle(this.plan.coreColor, 0.5);
    g.fillCircle(anchor.x, anchor.y, 22);
    g.setAlpha(0);
    await this.tween(g, { alpha: 0.9 }, Math.max(1, Math.round(this.plan.timings.travel * 0.5)));
    await this.tween(g, { alpha: 0 }, this.plan.timings.fade);
  }

  // ── Category templates ──────────────────────────────────────────────────────

  /**
   * A glowing beam/projectile from→to: an outer type-coloured line + a bright
   * inner core, with a particle stream along the path, then an impact burst.
   */
  private async beam(
    color: number,
    core: number,
    o: { thick?: number; big?: boolean } = {},
  ): Promise<void> {
    const t = this.plan.timings;
    const { from, to } = this.opts;
    const thick = o.thick ?? 7;

    const g = this.gfx();
    if (!g) return;

    // Draw the beam progressively by animating a 0→1 "reveal" along the path.
    const progress = { v: 0 };
    const redraw = (): void => {
      if (!g.active) return;
      g.clear();
      const ex = from.x + (to.x - from.x) * progress.v;
      const ey = from.y + (to.y - from.y) * progress.v;
      // Outer glow.
      g.lineStyle(thick, color, 0.45);
      g.lineBetween(from.x, from.y, ex, ey);
      // Bright core.
      g.lineStyle(Math.max(2, thick * 0.45), core, 0.95);
      g.lineBetween(from.x, from.y, ex, ey);
    };
    await this.tween(progress, { v: 1 }, t.travel, { onUpdate: redraw });

    // Stream particles along the beam toward the target.
    this.streamParticles(color, from, to, t.travel, this.plan.particleCount);

    // Beam holds a beat, then fades as the impact blooms.
    void this.tween(g, { alpha: 0 }, t.impact + t.fade);
    await this.impactBurst(core, { big: o.big });
  }

  /**
   * Physical: a quick lunge cue (a streak from→to), an impact FLASH + brief shake,
   * and a type-coloured hit spark at the target.
   */
  private async lungeHit(color: number): Promise<void> {
    const t = this.plan.timings;
    const { from, to } = this.opts;

    // Lunge streak: a short fading line in the strike direction.
    const streak = this.gfx();
    if (streak) {
      streak.lineStyle(5, color, 0.7);
      streak.lineBetween(from.x, from.y, to.x, to.y);
      void this.tween(streak, { alpha: 0 }, t.travel);
    }

    await this.wait(Math.round(t.travel * 0.6));

    // Impact flash: a quick white-hot disc at the target.
    const flash = this.gfx();
    if (flash) {
      flash.fillStyle(0xffffff, 0.85);
      flash.fillCircle(to.x, to.y, 26);
      flash.setAlpha(0);
      void this.tween(flash, { alpha: 1 }, Math.round(t.impact * 0.3));
      void this.tween(flash, { alpha: 0 }, t.impact, {
        delay: Math.round(t.impact * 0.3),
      });
    }

    // A brief camera shake (guarded — only if the camera is present).
    this.shake(Math.round(t.impact * 0.7), 0.006);

    // Type-coloured hit spark: a small radial burst of particles.
    this.burstParticles(color, to, Math.round(this.plan.particleCount * 0.8), 220);
    await this.wait(t.impact + t.fade);
  }

  /** Status: an aura/glow pulse on the USER (from) in the type colour; no projectile. */
  private async aura(color: number): Promise<void> {
    const t = this.plan.timings;
    const { from } = this.opts;

    const ring = this.gfx();
    if (ring) {
      const pulse = { r: 8, a: 0.0 };
      const redraw = (): void => {
        if (!ring.active) return;
        ring.clear();
        ring.fillStyle(color, pulse.a * 0.5);
        ring.fillCircle(from.x, from.y, pulse.r);
        ring.lineStyle(3, color, pulse.a);
        ring.strokeCircle(from.x, from.y, pulse.r);
      };
      // Ramp up, hold, ramp down — a calm two-beat pulse.
      const up = Math.round(t.travel * 0.4);
      const hold = Math.round(t.travel * 0.2);
      const down = Math.max(1, t.travel - up - hold);
      await this.tween(pulse, { r: 46, a: 0.9 }, up, { onUpdate: redraw });
      await this.wait(hold);
      await this.tween(pulse, { r: 58, a: 0 }, down, { onUpdate: redraw });
    }

    // A few rising motes around the user for life.
    this.risingMotes(color, from, Math.round(this.plan.particleCount * 0.6), t.travel);
    await this.wait(t.fade);
  }

  // ── Signature flourishes ────────────────────────────────────────────────────

  /** A growing charge glow at the user before a big beam (Hyper/Solar). */
  private async chargeGlow(color: number, ms: number): Promise<void> {
    if (ms <= 1) return;
    const { from } = this.opts;
    const g = this.gfx();
    if (!g) return;
    const c = { r: 4, a: 0 };
    const redraw = (): void => {
      if (!g.active) return;
      g.clear();
      g.fillStyle(color, c.a * 0.4);
      g.fillCircle(from.x, from.y, c.r);
      g.fillStyle(0xffffff, c.a * 0.5);
      g.fillCircle(from.x, from.y, c.r * 0.5);
    };
    this.risingMotes(color, from, Math.round(this.plan.particleCount * 0.5), ms);
    await this.tween(c, { r: 28, a: 1 }, ms, { onUpdate: redraw });
    void this.tween(g, { alpha: 0 }, this.plan.timings.fade);
  }

  /** Ice Beam / Blizzard: a scatter of cyan frost shards at the target. */
  private async frostShards(color: number): Promise<void> {
    const { to } = this.opts;
    const count = Math.max(3, Math.round(this.plan.particleCount * 0.4));
    const g = this.gfx();
    if (g) {
      for (let i = 0; i < count; i++) {
        const ang = (Math.PI * 2 * i) / count + Math.random() * 0.5;
        const len = 10 + Math.random() * 14;
        const x = to.x + Math.cos(ang) * len;
        const y = to.y + Math.sin(ang) * len;
        g.lineStyle(2, color, 0.9);
        g.lineBetween(to.x, to.y, x, y);
      }
      g.setAlpha(0);
      await this.tween(g, { alpha: 1 }, Math.round(this.plan.timings.impact * 0.3));
      await this.tween(g, { alpha: 0 }, this.plan.timings.fade);
    }
  }

  /** Flamethrower / Blizzard: a rolling stream of motes from→to. */
  private async fireStream(color: number, o: { swirl?: boolean } = {}): Promise<void> {
    const t = this.plan.timings;
    const { from, to } = this.opts;
    this.streamParticles(color, from, to, t.travel + t.impact, this.plan.particleCount, {
      spread: o.swirl ? 0.9 : 0.5,
      scale: { start: 0.9, end: 0.1 },
    });
    // A warm core flicker along the line.
    const g = this.gfx();
    if (g) {
      g.lineStyle(8, color, 0.4);
      g.lineBetween(from.x, from.y, to.x, to.y);
      g.lineStyle(3, 0xffffff, 0.6);
      g.lineBetween(from.x, from.y, to.x, to.y);
      void this.tween(g, { alpha: 0 }, t.travel + t.impact);
    }
    await this.wait(t.travel + t.impact);
    await this.impactBurst(color);
  }

  /** Thunderbolt / Thunder: jagged yellow bolt(s) snapping from→to (or above). */
  private async bolts(
    color: number,
    count: number,
    o: { fromAbove?: boolean } = {},
  ): Promise<void> {
    const t = this.plan.timings;
    const { from, to } = this.opts;
    const start = o.fromAbove ? { x: to.x, y: to.y - 220 } : from;

    const g = this.gfx();
    if (g) {
      for (let n = 0; n < count; n++) {
        drawJaggedBolt(g, start, to, color, n);
      }
      // Snap on, hold, flicker off — the electric staccato.
      g.setAlpha(0);
      await this.tween(g, { alpha: 1 }, Math.max(1, Math.round(t.travel * 0.2)));
      await this.wait(Math.round(t.travel * 0.3));
      await this.tween(g, { alpha: 0 }, t.impact);
    }
    this.shake(Math.round(t.impact * 0.6), 0.008);
    this.burstParticles(color, to, this.plan.particleCount, 260);
    await this.wait(t.fade);
  }

  /** A radial impact burst at the target (particles + a quick ring). */
  private async impactBurst(color: number, o: { big?: boolean } = {}): Promise<void> {
    const t = this.plan.timings;
    const { to } = this.opts;
    const r0 = o.big ? 14 : 8;
    const r1 = o.big ? 64 : 40;

    const ring = this.gfx();
    if (ring) {
      const c = { r: r0, a: 0.9 };
      const redraw = (): void => {
        if (!ring.active) return;
        ring.clear();
        ring.lineStyle(o.big ? 5 : 3, color, c.a);
        ring.strokeCircle(to.x, to.y, c.r);
      };
      void this.tween(c, { r: r1, a: 0 }, t.impact, { onUpdate: redraw });
    }
    this.burstParticles(color, to, this.plan.particleCount, o.big ? 320 : 220);
    await this.wait(t.impact + t.fade);
  }

  // ── Particle helpers ────────────────────────────────────────────────────────

  /** A directed stream of particles travelling from→to over `ms`. */
  private streamParticles(
    color: number,
    from: EffectPoint,
    to: EffectPoint,
    ms: number,
    count: number,
    o: { spread?: number; scale?: { start: number; end: number } } = {},
  ): void {
    if (!this.alive || count <= 0) return;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.max(1, Math.hypot(dx, dy));
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    const spreadDeg = (o.spread ?? 0.4) * 24;

    const emitter = this.particles(from.x, from.y, {
      lifespan: Math.max(60, ms * 0.8),
      speed: { min: (dist / (ms / 1000)) * 0.6, max: (dist / (ms / 1000)) * 1.1 },
      angle: { min: angle - spreadDeg, max: angle + spreadDeg },
      scale: o.scale ?? { start: 0.7, end: 0.05 },
      alpha: { start: 0.95, end: 0 },
      tint: color,
      quantity: 1,
      frequency: Math.max(8, ms / count),
      blendMode: Phaser.BlendModes.ADD,
    });
    if (!emitter) return;
    // Stop emitting after the travel window; the emitter is destroyed at teardown.
    this.after(ms, () => {
      if (emitter.active) emitter.stop();
    });
  }

  /** A one-shot radial spray (explosion) of particles at a point. */
  private burstParticles(color: number, at: EffectPoint, count: number, speed: number): void {
    if (!this.alive || count <= 0) return;
    const emitter = this.particles(at.x, at.y, {
      lifespan: 360,
      speed: { min: speed * 0.4, max: speed },
      scale: { start: 0.8, end: 0.05 },
      alpha: { start: 1, end: 0 },
      tint: color,
      angle: { min: 0, max: 360 },
      blendMode: Phaser.BlendModes.ADD,
      emitting: false,
    });
    if (!emitter) return;
    emitter.explode(count, at.x, at.y);
  }

  /** Slow motes drifting up around a point (aura / charge life). */
  private risingMotes(color: number, at: EffectPoint, count: number, ms: number): void {
    if (!this.alive || count <= 0) return;
    const emitter = this.particles(at.x, at.y, {
      lifespan: Math.max(200, ms),
      speedY: { min: -60, max: -20 },
      speedX: { min: -18, max: 18 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.8, end: 0 },
      tint: color,
      quantity: 1,
      frequency: Math.max(16, ms / Math.max(1, count)),
      blendMode: Phaser.BlendModes.ADD,
    });
    if (!emitter) return;
    this.after(ms, () => {
      if (emitter.active) emitter.stop();
    });
  }

  // ── Low-level spawners (all tracked for teardown) ─────────────────────────────

  private gfx(): Phaser.GameObjects.Graphics | null {
    if (!this.alive) return null;
    const g = this.scene.add.graphics();
    g.setDepth(this.depth);
    this.objects.push(g);
    return g;
  }

  private particles(
    x: number,
    y: number,
    config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig,
  ): Phaser.GameObjects.Particles.ParticleEmitter | null {
    if (!this.alive) return null;
    const emitter = this.scene.add.particles(x, y, PARTICLE_TEX, config);
    emitter.setDepth(this.depth);
    this.objects.push(emitter);
    this.emitters.push(emitter);
    return emitter;
  }

  /** A tracked tween that resolves a promise on complete (or on teardown). */
  private tween(
    target: object,
    props: Record<string, number>,
    duration: number,
    extra: { onUpdate?: () => void; delay?: number } = {},
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.alive) {
        resolve();
        return;
      }
      const tw = this.scene.tweens.add({
        targets: target,
        ...props,
        duration: Math.max(1, duration),
        delay: extra.delay ?? 0,
        ease: 'Sine.easeInOut',
        ...(extra.onUpdate ? { onUpdate: extra.onUpdate } : {}),
        onComplete: () => resolve(),
        onStop: () => resolve(),
      });
      this.tweens.push(tw);
    });
  }

  /** A tracked one-shot timer (cleaned up at teardown). */
  private after(ms: number, fn: () => void): void {
    if (!this.alive) return;
    const timer = this.scene.time.delayedCall(Math.max(1, ms), () => {
      if (this.alive) fn();
    });
    this.timers.push(timer);
  }

  /** A pace-aware wait. */
  private wait(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.alive) {
        resolve();
        return;
      }
      const timer = this.scene.time.delayedCall(Math.max(1, ms), resolve);
      this.timers.push(timer);
    });
  }

  /** A guarded, gentle camera shake (skipped if no camera / reduced motion). */
  private shake(ms: number, intensity: number): void {
    if (!this.alive || this.plan.reduced) return;
    const cam = this.scene.cameras?.main;
    if (cam && typeof cam.shake === 'function') cam.shake(Math.max(1, ms), intensity);
  }

  // ── Teardown ──────────────────────────────────────────────────────────────────

  /** Resolve the run promise exactly once. */
  private finish(): void {
    if (this.done) return;
    const r = this.resolveRun;
    this.resolveRun = null;
    this.destroy();
    if (r) r();
  }

  /** Scene shut down mid-effect: tear everything down and resolve immediately. */
  private abort(): void {
    this.finish();
  }

  /**
   * Destroy every object/tween/timer/emitter this run created. Idempotent and
   * defensive: each removal is guarded so a half-dead scene never throws.
   */
  destroy(): void {
    if (this.done) {
      // Listeners are already removed on first destroy; nothing more to do.
      return;
    }
    this.done = true;

    // Detach our shutdown listeners (the scene's emitter may already be gone).
    try {
      this.scene.events?.off(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
      this.scene.events?.off(Phaser.Scenes.Events.DESTROY, this.onShutdown);
    } catch {
      /* scene emitter gone — fine */
    }

    for (const tw of this.tweens) {
      try {
        tw.remove();
      } catch {
        /* tween already gone */
      }
    }
    for (const timer of this.timers) {
      try {
        timer.remove(false);
      } catch {
        /* timer already gone */
      }
    }
    for (const em of this.emitters) {
      try {
        if (em.active) em.stop();
      } catch {
        /* emitter already gone */
      }
    }
    for (const obj of this.objects) {
      try {
        if (obj.active) obj.destroy();
      } catch {
        /* object already gone */
      }
    }
    this.tweens.length = 0;
    this.timers.length = 0;
    this.emitters.length = 0;
    this.objects.length = 0;
  }
}

// ── Module-level draw helpers (pure given a Graphics) ─────────────────────────────

/** Draw one jagged lightning bolt between two points with a few zig segments. */
function drawJaggedBolt(
  g: Phaser.GameObjects.Graphics,
  start: EffectPoint,
  end: EffectPoint,
  color: number,
  seed: number,
): void {
  const segs = 6;
  const dx = (end.x - start.x) / segs;
  const dy = (end.y - start.y) / segs;
  const jitter = 14;
  g.lineStyle(3, color, 0.95);
  g.beginPath();
  g.moveTo(start.x, start.y);
  for (let i = 1; i < segs; i++) {
    const off = (((seed * 13 + i * 7) % 11) / 11 - 0.5) * jitter * 2;
    const perp = Math.atan2(end.y - start.y, end.x - start.x) + Math.PI / 2;
    g.lineTo(start.x + dx * i + Math.cos(perp) * off, start.y + dy * i + Math.sin(perp) * off);
  }
  g.lineTo(end.x, end.y);
  g.strokePath();
}

/**
 * Ensure the shared 1×1 white particle texture exists in the scene's cache. We
 * generate a tiny soft dot once (then tint it per-effect), so the engine needs no
 * art asset. Safe to call repeatedly.
 */
function ensureParticleTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(PARTICLE_TEX)) return;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(4, 4, 4);
  g.fillStyle(0xffffff, 0.5);
  g.fillCircle(4, 4, 8 / 2);
  g.generateTexture(PARTICLE_TEX, 8, 8);
  g.destroy();
}

export type { EffectPlan, EffectTimings };
